/**
 * Panel de Control People — panel propio de People, pensado para crecer módulo
 * a módulo. Primer módulo: el proceso de onboarding (People-flow + pasos IT).
 * Secciones por query param (?tab=&alta= permite deep links desde
 * notificaciones). Rediseño 16/09 (Sol/KP): la navegación principal son 4
 * cards clickeables como las del dashboard — Start Next 7 Days / Process /
 * Errors / Completed — y Enviados / Salud / Configuración quedan como links
 * secundarios. Kits se fue (vive en Office Manager). Solo ingresos desde
 * PANEL_SINCE (07/09/2026).
 */

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, ListChecks, UserPlus } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';
import { OnboardingUpcomingTab } from './OnboardingUpcomingTab';
import { OnboardingProcessTab } from './OnboardingProcessTab';
import { OnboardingCompletedTab } from './OnboardingCompletedTab';
import { OnboardingErrorsTab } from './OnboardingErrorsTab';
import { OnboardingSentTab } from './OnboardingSentTab';
import { OnboardingHealthTab } from './OnboardingHealthTab';
import { OnboardingConfigTab } from './OnboardingConfigTab';
import { PANEL_SINCE, usePeopleFlowRows } from './usePeopleFlowRows';

const MAIN = ['upcoming', 'proceso', 'errores', 'completados'] as const;
const SECONDARY = ['enviados', 'salud', 'config'] as const;
type Tab = (typeof MAIN)[number] | (typeof SECONDARY)[number];
const ALL_TABS: readonly string[] = [...MAIN, ...SECONDARY];

/** Card de navegación principal: como las del dashboard, al clic lleva a la sección. */
function SectionCard({ icon: Icon, label, value, hint, active, alert, onClick }: {
  icon: typeof UserPlus;
  label: string;
  value: number;
  hint: string;
  active: boolean;
  alert?: boolean;
  onClick: () => void;
}) {
  return (
    <Card
      role="button"
      tabIndex={0}
      aria-pressed={active}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      className={cn(
        'cursor-pointer p-4 transition-colors hover:bg-muted/50',
        active && 'ring-2 ring-primary',
        alert && value > 0 && 'border-red-200 dark:border-red-900/60'
      )}
    >
      <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Icon className={cn('h-4 w-4', alert && value > 0 && 'text-red-600 dark:text-red-400')} />
        {label}
      </p>
      <p className={cn('mt-1 text-2xl font-bold tabular-nums', alert && value > 0 && 'text-red-700 dark:text-red-400')}>{value}</p>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p>
    </Card>
  );
}

export function PeoplePanelPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const tab: Tab = rawTab && ALL_TABS.includes(rawTab) ? (rawTab as Tab) : 'proceso';

  // Los contadores de las cards salen de las mismas filas que las tablas
  const { buckets } = usePeopleFlowRows();

  // Freshness de la última corrida, visible en el header de todo el panel
  const { data: runs } = trpc.peopleFlow.getRunHistory.useQuery(undefined, { staleTime: 60_000 });
  const lastRun = useMemo(() => runs?.[0] ?? null, [runs]);

  const setTab = (next: Tab) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    params.delete('alta');
    setSearchParams(params, { replace: true });
  };

  const k = 'users.people_panel';
  const sinceLabel = `${PANEL_SINCE.slice(8, 10)}/${PANEL_SINCE.slice(5, 7)}/${PANEL_SINCE.slice(0, 4)}`;

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold">{t(`${k}.title`)}</h1>
        <p className="text-muted-foreground">{t(`${k}.subtitle`)}</p>
      </div>

      {/* Navegación principal: 4 cards clickeables (Sol/KP 16/09) */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SectionCard icon={UserPlus} label={t(`${k}.cards.upcoming`, 'Start Next 7 Days')} value={buckets.upcoming.length} hint={t(`${k}.cards.upcoming_hint`, 'Quién entra en los próximos 7 días, por clapbase')} active={tab === 'upcoming'} onClick={() => setTab('upcoming')} />
        <SectionCard icon={ListChecks} label={t(`${k}.cards.process`, 'Process')} value={buckets.active.length} hint={t(`${k}.cards.process_hint`, { since: sinceLabel, defaultValue: 'Onboardings en curso · ingresos desde el {{since}}' })} active={tab === 'proceso'} onClick={() => setTab('proceso')} />
        <SectionCard icon={AlertTriangle} label={t(`${k}.cards.errors`, 'Errors')} value={buckets.errors.length} hint={t(`${k}.cards.errors_hint`, 'Pasos fallados o datos faltantes que necesitan tu atención')} active={tab === 'errores'} alert onClick={() => setTab('errores')} />
        <SectionCard icon={CheckCircle2} label={t(`${k}.cards.completed`, 'Completed')} value={buckets.completed.length} hint={t(`${k}.cards.completed_hint`, 'Ingresaron y todos sus pasos quedaron cerrados')} active={tab === 'completados'} onClick={() => setTab('completados')} />
      </div>

      {/* Secundarias: operación del motor */}
      <div className="flex flex-wrap items-center gap-1 border-b pb-2 text-sm">
        {SECONDARY.map((s) => (
          <button
            key={s}
            onClick={() => setTab(s)}
            className={cn(
              'rounded-md px-3 py-1.5 transition-colors',
              tab === s ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {s === 'enviados' ? t(`${k}.tabs.sent`, 'Enviados') : s === 'salud' ? t(`${k}.tabs.health`) : t(`${k}.tabs.config`)}
          </button>
        ))}
      </div>

      {tab === 'upcoming' && <OnboardingUpcomingTab lastRunAt={lastRun?.createdAt ?? null} />}
      {tab === 'proceso' && <OnboardingProcessTab lastRunAt={lastRun?.createdAt ?? null} />}
      {tab === 'errores' && <OnboardingErrorsTab />}
      {tab === 'completados' && <OnboardingCompletedTab />}
      {tab === 'enviados' && <OnboardingSentTab />}
      {tab === 'salud' && <OnboardingHealthTab />}
      {tab === 'config' && <OnboardingConfigTab />}
    </div>
  );
}
